    import { MapPin } from 'lucide-react';
import { AppLink } from '@/shared/components/AppLink';
import type { StoreWidgetData } from './SelectedStoreWidget';

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getStoresForCity(
  cityToStoresMap: unknown,
  cityName: string
): StoreWidgetData[] {
  if (!isObject(cityToStoresMap) || !cityName) return [];
  const normalized = cityName.trim().toLowerCase();
  for (const key of Object.keys(cityToStoresMap)) {
    if (key.trim().toLowerCase() === normalized) {
      const arr = cityToStoresMap[key];
      return Array.isArray(arr) ? (arr as StoreWidgetData[]) : [];
    }
  }
  return [];
}

export function OtherStoresWidget({
  cityToStoresMap,
  selectedStoreData,
  selectedStoresData,
  isCityPage = false,
  cityName: cityNameProp,
}: {
  cityToStoresMap: unknown;
  selectedStoreData?: StoreWidgetData | null;
  selectedStoresData?: StoreWidgetData[];
  /** True on city-level store catalog pages (no specific store selected). */
  isCityPage?: boolean;
  /** City display name for city pages (derived from URL slug when selectedStoreData is null). */
  cityName?: string;
}) {
  const cityName = selectedStoreData?.cityName ?? cityNameProp ?? '';
  const cityStores = getStoresForCity(cityToStoresMap, cityName);

  const selectedSids = new Set(
    (selectedStoresData ?? [])
      .map((s) => s.sid?.trim())
      .filter(Boolean) as string[]
  );

  // City page: show ALL stores; store page: exclude currently selected store(s)
  const storesToShow = isCityPage
    ? cityStores
    : cityStores.filter((store) => !selectedSids.has(store.sid?.trim() ?? '__none__'));

  const hasStores = storesToShow.length > 0;
  const hasCityContent = isCityPage && cityName;

  if (!cityName || (!hasStores && !hasCityContent)) return null;
  if (!isCityPage && storesToShow.length === 0) return null;

  return (
    <div className="mx-4 my-6 md:mx-8">
      {hasStores && (
        <>
          <h2 className="mb-4 text-xl font-bold text-[#0d2750]">
            {isCityPage
              ? `Locate BlueStone Jewellery Shops in ${cityName}`
              : 'Other BlueStone Jewellery Stores Nearby'}
          </h2>
          <div className="md:max-h-[250px] md:overflow-y-auto">
            <ul className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {storesToShow.map((store) => (
                <li key={store.sid ?? store.id}>
                  <AppLink
                    to={`/stores/${store.storeUrl}/catalog.html`}
                    shouldRefresh={false}
                    className="flex h-full items-start gap-3 rounded-xl bg-[#f0f4f4] px-4 py-4 text-sm text-gray-800 hover:bg-[#e4ecec] transition-colors"
                  >
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[#0d2750]" />
                    <span>
                      <strong className="font-semibold text-[#0d2750]">
                        {store.storeName}
                      </strong>{' '}
                      {store.address}
                    </span>
                  </AppLink>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
      {hasCityContent && (
        <div className="mt-8">
          <h2 className="mb-4 text-xl font-bold text-[#0d2750]">
            Best Jewellery Stores in {cityName} - BlueStone
          </h2>
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>
              The BlueStone jewellery store in {cityName} is one of the best jewellery store for Gold &amp; Diamond jewellery
              shopping. BlueStone jewellery in {cityName} is known for its craftsmanship and offers a wide range of designs
              suitable for every occasion, from everyday wear to special celebrations. Customers can explore a diverse
              selection of fine jewellery including engagement rings, necklaces, earrings, and more. The store also offers
              repair and personalized services, such as jewellery customization and expert consultations, to ensure you
              find the perfect piece that matches your style and preferences.
            </p>
            <p>
              For those in search of beautiful and unique jewellery design in {cityName}, the BlueStone Jewellery showroom
              in {cityName} is an excellent choice. Visit the BlueStone store near you in {cityName} to experience
              top-notch customer service and discover the perfect addition to your jewellery collection.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
